import { useCallback, useEffect, useMemo, useState } from 'react';

import { DateTime } from 'luxon';
import { isEqual, pickBy } from 'lodash';

export interface OpsMapParams {
  date?: string;
  region?: number;
  order?: number;
  dispatch?: number;
  checkin?: number;
  account_id?: number;
}

export type OpsMapGoOptions = {
  preserveFocus?: boolean;
};

const URL_KEYS: (keyof OpsMapParams)[] = ['date', 'region', 'order', 'dispatch', 'checkin', 'account_id'];

const parseIntParam = (value: string | null): number | undefined => {
  if (!value || !/^\d+$/.test(value)) return undefined;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const parseParamsFromSearch = (search: string): OpsMapParams => {
  const params = new URLSearchParams(search);
  const result: OpsMapParams = {};

  const date = params.get('date');
  if (date) result.date = date;

  const region = parseIntParam(params.get('region'));
  if (region !== undefined) result.region = region;

  const order = parseIntParam(params.get('order'));
  if (order !== undefined) result.order = order;

  const dispatch = parseIntParam(params.get('dispatch'));
  if (dispatch !== undefined) result.dispatch = dispatch;

  const checkin = parseIntParam(params.get('checkin'));
  if (checkin !== undefined) result.checkin = checkin;

  const accountId = parseIntParam(params.get('account_id'));
  if (accountId !== undefined) result.account_id = accountId;

  return result;
};

export const syncParamsToUrl = (params: OpsMapParams) => {
  const url = new URL(window.location.href);

  URL_KEYS.forEach((key) => url.searchParams.delete(key));

  if (params.date) url.searchParams.set('date', params.date);
  if (params.region !== undefined) url.searchParams.set('region', String(params.region));
  if (params.order !== undefined) url.searchParams.set('order', String(params.order));
  if (params.dispatch !== undefined) url.searchParams.set('dispatch', String(params.dispatch));
  if (params.checkin !== undefined) url.searchParams.set('checkin', String(params.checkin));
  if (params.account_id !== undefined) url.searchParams.set('account_id', String(params.account_id));

  window.history.replaceState({}, '', url.toString());
};

const pickDefined = (params: Partial<OpsMapParams>): OpsMapParams =>
  pickBy(params, (value) => value !== undefined && value !== null) as OpsMapParams;

const defaultParams = (regionIds: number[]): OpsMapParams => ({
  date: DateTime.now().toFormat('yyyy-MM-dd'),
  region: regionIds[0] ?? 1,
});

export const useOpsMapUrlState = (regionIds: number[]) => {
  const [stateOverrides, setStateOverrides] = useState<Partial<OpsMapParams>>({});
  const [ready, setReady] = useState(false);

  const mergedParams = useCallback((): OpsMapParams => {
    return {
      ...parseParamsFromSearch(window.location.search),
      ...pickDefined(stateOverrides),
    };
  }, [stateOverrides]);

  const go = useCallback(
    (params: OpsMapParams, options?: OpsMapGoOptions) => {
      const current = mergedParams();
      let newParams: OpsMapParams = { ...params };

      if (
        !options?.preserveFocus &&
        ((current.date && newParams.date !== current.date) ||
          (current.region !== undefined && String(newParams.region) !== String(current.region)))
      ) {
        newParams = {
          date: newParams.date,
          region: newParams.region,
        };
      }

      setStateOverrides(newParams);
      syncParamsToUrl(newParams);
    },
    [mergedParams],
  );

  useEffect(() => {
    const current = mergedParams();
    const withDefaults = { ...defaultParams(regionIds), ...current };

    if (!isEqual(withDefaults, current)) {
      go(withDefaults);
    }

    setReady(true);
  }, [go, mergedParams, regionIds]);

  const params = useMemo(() => mergedParams(), [mergedParams]);

  return { params, go, ready };
};
